<template>
  <v-app-bar
    v-bind="$attrs"
    v-on="$listeners"
    :app="app"
    :fixed="fixed"
    :clipped-left="clippedLeft"
    :flat="flat"
    :dark="dark"
    :color="color"
    :extension-height="extensionHeight"
    class="justify-space-between"
  >
    <slot
      name="backButton"
      v-if="showBackButton"
    >
      <v-btn
        icon
        class="ml-3 mr-2"
        @click="$router.go(-1)"
        aria-label="go back"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
    </slot>

    <v-app-bar-nav-icon
      tabindex="1"
      v-if="showNavIcon && !showBackButton"
      @click.stop="handleClickNavigation"
      class="navigation-drawer-toggle"
      aria-label="Toggle navigation menu"
      :aria-expanded="drawer ? 'true' : 'false'"
    />

    <slot name="appIconContainer">
      <span v-if="enableHomeLink">
        <router-link
          :to="homeLink"
          style="text-decoration: none; color: inherit;"
          aria-label="App home"
          class="app-icon"
        >
          <v-toolbar-title class="pl-0">
            <slot name="appIcon" />
          </v-toolbar-title>
        </router-link>
      </span>
      <span v-else>
        <v-toolbar-title class="pl-0">
          <slot name="appIcon" />
        </v-toolbar-title>
      </span>
    </slot>
    <slot name="title">
      <span
        class="title--width"
        :class="expandSearch ? 'shrinkTitle' : 'expandTitle'"
        v-if="enableHomeLink"
      >
        <router-link
          :to="homeLink"
          style="text-decoration: none; color: inherit;"
          :aria-label="title"
          class="app-title"
        >
          <v-toolbar-title class="pl-0">
            <div class="appbar-title px-2">
              <h1>{{ title }}</h1>
            </div>
          </v-toolbar-title>
        </router-link>
      </span>
      <span
        class="title--width"
        :class="expandSearch ? 'shrinkTitle' : 'expandTitle'"
        v-else
      >
        <v-toolbar-title
          class="pl-0"
          :aria-label="title"
          tabindex="0"
        >
          <div class="appbar-title px-2">
            <h1>{{ title }}</h1>
          </div>
        </v-toolbar-title>
      </span>
    </slot>
    <v-spacer
      v-if="!expandSearch"
      id="appBarSpacer"
    />
    <div
      class="mr-2"
      id="dataSwitcherContainer"
      :class="$vuetify.breakpoint.xsOnly ? 'xs': $vuetify.breakpoint.mdOnly ? 'md' : 'lg'"
      v-if="$slots.dataSwitcher"
    >
      <slot name="dataSwitcher" />
    </div>
    <div
      v-if="showSearch"
      :style="$vuetify.breakpoint.xsOnly ? 'flex-shrink:1 !important':'flex-shrink:0 !important'"
      :class="expandSearch ? 'ml-2 grow' : 'ml-2'"
    >
      <slot name="search">
        <Search
          searchWidth="100%"
          :searchUrl="searchUrl"
          :globalSearch="true"
          @searchClick="searchClick"
        />
      </slot>
    </div>
    <v-toolbar-title
      class="ml-2"
      style="min-width: 48px;"
      v-if="showFullScreen && $vuetify.breakpoint.mdAndUp"
    >
      <FullScreen />
    </v-toolbar-title>
    <div
      class="mr-2"
      v-if="showNotifications"
    >
      <span role="button">
        <v-btn
          @click.stop="$eventHub.$emit('toggle:notifications')"
          aria-label="Toggle notifications"
          icon
        >
          <v-badge
            dot
            v-if="newNotifications"
            color="error"
            overlap
          >
            <v-icon class="material-icons-outlined">notifications</v-icon>
          </v-badge>
          <v-icon
            v-else
            class="material-icons-outlined"
          >notifications</v-icon>
        </v-btn>
      </span>
    </div>
    <div
      class="mr-2"
      v-if="$slots.myEssentials"
    >
      <slot name="myEssentials" />
    </div>
    <div
      class="mr-6"
      v-if="$slots.profile"
    >
      <slot name="profile" />
    </div>
    <template
      v-slot:extension
      v-if="extended"
    >
      <slot name="extension" />
    </template>
  </v-app-bar>
</template>

<script>
import FullScreen from '../FullScreen';
import Search from '../Search/index';
import {ADS_Colors} from '../ads-colors';

export default {
  name: 'AppBar',
  components: {
    FullScreen,
    Search
  },
  props: {
    color: {
      type: String,
      default: ADS_Colors.DP02   // override the default value 'undefined' in v-app-bar
    },
    title: {
      type: String,
      default: ''
    },
    showFullScreen: {
      type: Boolean,
      default: false
    },
    showNavIcon: {
      type: Boolean,
      default: true
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    extended: {
      type: Boolean,
      default: false
    },
    showSearch: {
      type: Boolean,
      default: false
    },
    newNotifications: {
      type: Boolean,
      default: false
    },
    showNotifications: {
      type: Boolean,
      default: false
    },
    searchUrl: {
      type: String,
      default: ''
    },
    homeLink: {
      type: String,
      default: '/'
    },
    enableHomeLink: {
      type: Boolean,
      default: true
    },
    app: {
      type: Boolean,
      default: true   // override the default value 'false' in v-app-bar
    },
    fixed: {
      type: Boolean,
      default: true   // override the default value 'false' in v-app-bar
    },
    clippedLeft: {
      type: Boolean,
      default: true   // override the default value 'false' in v-app-bar
    },
    flat: {
      type: Boolean,
      default: true   // override the default value 'false' in v-app-bar
    },
    dark: {
      type: Boolean,
      default: true   // override the default value 'false' in v-app-bar
    },
    extensionHeight: {
      type: [Number, String],
      default: '56'   // override the default value '48' in v-app-bar
    }
  },
  data() {
    return {
      searchString: '',
      expandSearch: false,
      searchContent: '',
      drawer: this.$vuetify.breakpoint.lgAndUp ? true : false
    }
  },
  methods: {
    searchClick() {
      this.expandSearch = !this.expandSearch;
    },
    handleClickNavigation() {
      this.drawer = !this.drawer;
      this.$eventHub.$emit('toggle:navigation');
    },
    updateDrawerState() {
      this.drawer = false;
    }
  },
  mounted() {
    // adding eventHub listener
    this.$eventHub.$on('closed:navigation', this.updateDrawerState);
  },
  beforeDestroy() {
    // removing eventHub listener
    this.$eventHub.$off('closed:navigation');
  }
};
</script>

<style lang="scss" scoped>
@import '../../styles/variables.scss';

#dataSwitcherContainer.xs{
    min-width:50px;
  }
#dataSwitcherContainer.md{
    min-width:150px;
}
#dataSwitcherContainer.lg{
    min-width:250px;
}
.title--width {
  width: 25%;
}

.appbar-title {
  vertical-align: middle;
  line-height: 40px;

  h1 {
    font-size: 1.25rem;
    font-weight: 700;
  }
}

.navigation-drawer-toggle {
  margin-left: 12px !important;
  margin-right: 8px !important;

  &:focus {
    border: 2px solid $ads-white !important;
    outline: none !important;
  }
}

.app-icon {
  &:focus {
    outline: none;

    .v-toolbar__title {
      outline: 2px solid $ads-white;
    }
  }
}

.app-title {
  &:focus {
    outline: none;

    .v-toolbar__title {
      outline: 2px solid $ads-white;
    }
  }
}

.v-btn {
  &:focus {
    outline: none;
    border: 2px solid $ads-white;
  }
}

::v-deep .v-toolbar__content, ::v-deep .v-toolbar__extension {
  padding: 0px;
}

::v-deep #schoolPickerContainer .v-input__control .v-input__slot {
  background-color: $ads-custom-navy-3;
}

.neutralSearch {
  width: 50px;
}

.shrinkTitle {
  width: 0px;
  transition: width 0.1s;
}

.expandTitle {
  width: 43%;
  transition: width 0.5s;
}

::v-deep .hideBorder.v-text-field--outlined fieldset {
  border: none !important;
}

::v-deep .v-toolbar__content {
  -webkit-box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4) ;
  -moz-box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4) ;
  box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4) ;
}

</style>
